/**
 * UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2 — shared topbar utility action;
 * Sakhtar Pilot P0 — informational dialog, implemented with the canonical V2Modal.
 * The PO requested page-specific training copy and Sepidar/Holoo familiar terms.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { V2Badge, V2Button, V2Modal } from '@sakhtar/design-system/react';
import { GENERAL_FALLBACK_GUIDE } from './generalGuides.js';
import { resolvePageHelp } from './registry.ts';
import { HELP_COMPARISONS } from './comparisons.js';
import type { PageHelpGuide } from './types.js';
import './pageHelp.css';

const PageHelpContext = createContext<{
  readonly guide: PageHelpGuide;
  readonly open: boolean;
  readonly show: () => void;
} | null>(null);

export function PageHelpButton() {
  const help = useContext(PageHelpContext);
  if (!help) return null;
  return (
    <V2Button
      variant="secondary"
      size="sm"
      icon="info"
      className="page-help-trigger"
      aria-label="راهنمای این صفحه"
      title={`راهنمای ${help.guide.title}`}
      aria-haspopup="dialog"
      aria-expanded={help.open}
      onClick={help.show}
    >
      <span className="page-help-trigger__full">راهنمای این صفحه</span>
      <span className="page-help-trigger__short" aria-hidden="true">
        راهنما
      </span>
    </V2Button>
  );
}

function FamiliarTerms({ guide }: { readonly guide: PageHelpGuide }) {
  const comparisons = guide.comparison ? HELP_COMPARISONS[guide.comparison] : undefined;
  if (!comparisons?.length) return null;
  return (
    <section className="page-help__section" aria-label="نام‌های آشنا در سپیدار و هلو">
      <h3>قبلاً با سپیدار یا هلو کار کرده‌اید؟</h3>
      <dl className="page-help__comparisons">
        {comparisons.map((item) => (
          <div className="page-help__comparison" key={item.product}>
            <dt>{item.product}</dt>
            <dd>
              <strong>{item.term}</strong>
              <p>{item.explanation}</p>
            </dd>
          </div>
        ))}
      </dl>
      <p className="page-help__muted">
        این نام‌ها کمک می‌کنند مفهوم آشنا را پیدا کنید. مراحل کار را از راهنمای ساختار دنبال کنید؛
        نام و امکانات بخش‌ها در نسخه‌های مختلف نرم‌افزار قبلی متفاوت است.
      </p>
    </section>
  );
}

function ProcessPosition({ guide }: { readonly guide: PageHelpGuide }) {
  if (!guide.flows?.length) return null;
  return (
    <section className="page-help__section" aria-label="جای این صفحه در فرایند">
      <h3>جای این صفحه در فرایند</h3>
      <p className="page-help__muted">
        این مسیر جایگاه صفحه را نشان می‌دهد؛ وضعیت واقعی هر سند یا کار را از همان رکورد بخوانید.
      </p>
      <div className="page-help__flows">
        {guide.flows.map((flow, index) => (
          <details className="page-help__flow" key={flow.id} open={index === 0}>
            <summary>
              <strong>{flow.title}</strong>
              <span className="page-help__muted">
                {flow.role === 'overview'
                  ? 'نمای کلی فرایند'
                  : flow.role === 'support'
                    ? 'نقش پشتیبان این صفحه'
                    : 'جای این صفحه'}
              </span>
            </summary>
            <div className="page-help__flow-body">
              <p>{flow.position}</p>
              <ol className="page-help__flow-steps" aria-label={`مراحل ${flow.title}`} role="list">
                {flow.steps.map((step, stepIndex) => {
                  const current = step.id === flow.currentStepId;
                  return (
                    <li key={step.id} aria-current={current ? 'step' : undefined}>
                      <span className="page-help__step-number" aria-hidden="true">
                        {(stepIndex + 1).toLocaleString('fa-IR')}
                      </span>
                      <strong>{step.label}</strong>
                      {current ? (
                        <V2Badge tone="brand">
                          {flow.role === 'support' ? 'نقش این صفحه' : 'این صفحه'}
                        </V2Badge>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
              <dl className="page-help__flow-handoffs">
                <div>
                  <dt>ورودی و پیش‌نیاز</dt>
                  <dd>{flow.before}</dd>
                </div>
                <div>
                  <dt>خروجی و قدم بعدی</dt>
                  <dd>{flow.after}</dd>
                </div>
              </dl>
              {flow.note ? <p className="page-help__muted">{flow.note}</p> : null}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}

/** Render beside the app shell, so fixed dialogs are outside the topbar's glass layer. */
export function PageHelpProvider({
  pathname,
  tenantSlug,
  children,
}: {
  readonly pathname: string;
  readonly tenantSlug: string;
  readonly children: ReactNode;
}) {
  const routeKey = `${tenantSlug}:${pathname}`;
  const [openFor, setOpenFor] = useState<string | null>(null);
  const guide = resolvePageHelp(pathname, tenantSlug) ?? GENERAL_FALLBACK_GUIDE;
  const open = openFor === routeKey;
  useEffect(() => {
    setOpenFor(null);
  }, [routeKey]);
  const close = () => setOpenFor(null);

  return (
    <PageHelpContext.Provider value={{ guide, open, show: () => setOpenFor(routeKey) }}>
      {children}
      <div className="sk-app page-help">
        <V2Modal
          open={open}
          title={`راهنمای ${guide.title}`}
          onClose={close}
          size="lg"
          footer={
            <V2Button variant="primary" onClick={close}>
              متوجه شدم
            </V2Button>
          }
        >
          <div className="page-help__content" data-help-id={guide.id}>
            <section className="page-help__section" aria-label="کاربرد این صفحه">
              <h3>اینجا چه کاری انجام می‌دهم؟</h3>
              <p>{guide.purpose}</p>
            </section>
            <ProcessPosition guide={guide} />
            <FamiliarTerms guide={guide} />
            <section className="page-help__example" aria-label="مثال آموزشی">
              <h3>یک مثال ساده</h3>
              <p>{guide.example}</p>
            </section>
            <section className="page-help__section" aria-label="مراحل کار">
              <h3>از کجا شروع کنم؟</h3>
              <ol>
                {guide.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </section>
            <section className="page-help__section" aria-label="نتیجهٔ کار">
              <h3>بعدش چه می‌شود؟</h3>
              <p>{guide.result}</p>
            </section>
            <section className="page-help__tips" aria-label="نکته‌های مهم">
              <h3>حواسم به چه چیزی باشد؟</h3>
              <ul>
                {guide.tips.map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
              </ul>
            </section>
          </div>
        </V2Modal>
      </div>
    </PageHelpContext.Provider>
  );
}
